import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import { Button, Flexbox } from '@ibinder/common-frontend';
import ListDocument from '../ListDocument';
import {
  GET_USER_RESOURCE_ACCESS,
  UserResourceAccessResponse
} from './getUserResourceAccess';

type Props = {
  userId: string;
  userName: string;
  onBack: () => void;
};

type ResourceAccessRow = {
  id: string;
  name: string;
  type: 'Organization' | 'Binder' | 'Tender';
  organization: string;
  organizationId: string;
  role: string;
};

type SortBy = {
  id?: string;
  desc: boolean | null;
};

const Container = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled(Flexbox)`
  align-items: center;
  margin-bottom: 1rem;
`;

const BackButtonContainer = styled(Flexbox)`
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 1rem;
`;

const BackButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  margin: 0;
`;

const StatusMessage = styled.p`
  margin: 1rem 0;
`;

const accessColumns = [
  {
    Header: 'Type',
    accessor: 'type',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Name',
    accessor: 'name',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Id',
    accessor: 'id',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Organization',
    accessor: 'organization',
    sortType: 'alphanumeric'
  },
  {
    Header: 'OrganizationId',
    accessor: 'organizationId',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Role',
    accessor: 'role',
    sortType: 'alphanumeric'
  }
];

const normalizeResponse = (
  response: UserResourceAccessResponse
): ResourceAccessRow[] => [
  ...response.organizations.map((organization) => ({
    id: organization.id,
    name: organization.name,
    type: 'Organization' as const,
    organization: '',
    organizationId: '',
    role: organization.participantType
  })),
  ...response.binders.map((binder) => ({
    id: binder.id,
    name: binder.name,
    type: 'Binder' as const,
    organization: binder.organization.name,
    organizationId: binder.organization.id,
    role: binder.participantType
  })),
  ...response.tenders.map((tender) => ({
    id: tender.id,
    name: tender.name,
    type: 'Tender' as const,
    organization: tender.organization.name,
    organizationId: tender.organization.id,
    role: tender.participantType
  }))
];

const UserResourceAccess = ({ userId, userName, onBack }: Props) => {
  const [sortBy, setSortBy] = useState<SortBy>({
    id: 'name',
    desc: false
  });
  const [
    loadUserResourceAccess,
    { data: resourceAccessData, loading, error, called }
  ] = useLazyQuery(GET_USER_RESOURCE_ACCESS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    loadUserResourceAccess({ variables: { userId } });
  }, [userId]);

  const data = useMemo(
    () =>
      resourceAccessData?.getUserResourceAccess
        ? normalizeResponse(
            resourceAccessData.getUserResourceAccess as UserResourceAccessResponse
          )
        : [],
    [resourceAccessData]
  );
  const isLoading = !called || loading;

  return (
    <Container>
      <Header>
        <Title>{`Access for ${userName}`}</Title>
      </Header>

      {error ? (
        <StatusMessage>Unable to load user access.</StatusMessage>
      ) : !isLoading && data.length === 0 ? (
        <StatusMessage>This user does not have access to any resources.</StatusMessage>
      ) : (
        <ListDocument
          data={data}
          columns={accessColumns}
          onSort={setSortBy}
          sortBy={sortBy}
          selectedIds={[]}
          setSelectedIds={() => undefined}
          hasSelectBox={false}
          fetchMore={() => undefined}
          hasFetchMore={() => false}
          loading={isLoading}
          type="UserResourceAccess"
          truncateFirstColumn={false}
          fullContentColumnIds={['name']}
        />
      )}
      <BackButtonContainer>
        <BackButton btnStyle="secondary" label="Back" onClick={onBack} />
      </BackButtonContainer>
    </Container>
  );
};

export default UserResourceAccess;
